import {roundToAgencyFigure, suggestedMonthlyFor} from './budgetMath';

/**
 * El ritmo del mes en curso contra el mes pasado, para el paso 4 (C-11, opción A — fede, 15/10).
 *
 * Vive aparte de `budgetMath` porque lo lee también `StepBudgets` (el resumen de arriba), y las suites
 * de ese paso mockean `budgetMath` entero.
 */

// Desde qué desvío se avisa: la sensibilidad de «advertencia» de fábrica de la alerta de presupuesto
// (`_medium_alert_sensitivity` = 0,15; la crítica, `_high_…`, es 0,25). Es desde donde el semáforo deja
// el verde. Una agencia puede configurar otras: acá vale la de fábrica.
export const PACE_ALERT_RATIO = 0.15;
// Desde qué desvío el semáforo pasa a rojo: la sensibilidad «crítica» de fábrica
// (`_high_alert_sensitivity` = 0,25). Entre las dos, la línea nace en ámbar.
export const PACE_CRITICAL_RATIO = 0.25;

/**
 * ¿Este mes la cuenta va a otro ritmo que el mes pasado? (C-11, opción A — decisión de fede, 15/10)
 *
 * El paso 4 propone el gasto del mes pasado, y si este mes la cuenta gasta muy distinto, la línea
 * nace en rojo en la primera pantalla del trial: el semáforo compara bien contra un monto que propuso
 * Dashbo y nadie eligió. El backend manda `this_month` (lo gastado en los días completos del mes y la
 * proyección al cierre) sólo junto al gasto observado y con al menos 5 días para proyectar.
 *
 * Devuelve `null` si no hay que avisar: sin `this_month`, si con el monto del mes pasado el semáforo
 * seguiría en verde, o si el usuario escribió SU número (gana). Si no, `{spent, days, projected,
 * zero, last, slower, level, chosen}`:
 * - `projected` y `last`, los dos montos entre los que se elige, redondeados como cualquier
 *   sugerencia. `zero` es «este mes no invirtió»: ahí no hay un monto que ofrecer.
 * - `slower` dice hacia dónde va («más lento» o «más rápido»): «otro ritmo» no lo decía (UD-22).
 * - `level` es el color con que nacería la línea con el monto del mes pasado: `red` o `amber`.
 * - `chosen` es la opción marcada: `pace` si el monto editado es justo la proyección (se eligió
 *   DESDE el aviso, y el aviso queda para poder volver), `last` si no hay edición.
 */
export function paceNoticeOf(suggestion, edit) {
    const pace = suggestion?.this_month;
    if (!pace) return null;
    // El aviso mide LO MISMO que el semáforo (review C-cards-recientes-02): `|gasto − esperado a la
    // fecha| / presupuesto` (`entities/alerts/full_period/budget.py`), con el presupuesto con que nace la
    // línea. Como el gasto es `projected × días / días del mes`, eso es el desvío del ritmo escalado por
    // la parte del mes que pasó. Comparar sólo el ritmo avisaba de más al principio del mes (día 6, −50 %:
    // la línea nace verde) y de menos al final (día 29, −25 %: nace ámbar sin aviso). Sin gasto, el
    // semáforo da 100 % y rojo; acá da `días / días del mes`, que con 5 días o más ya pasa el umbral.
    const budget = suggestedMonthlyFor(suggestion);
    const projected = Number(pace.projected);
    const days = Number(pace.days);
    const daysInMonth = Number(pace.days_in_month);
    if (!budget || !Number.isFinite(projected) || !days || !daysInMonth) return null;
    const deviation = Math.abs(projected - budget) / budget * days / daysInMonth;
    if (deviation < PACE_ALERT_RATIO) return null;
    const rounded = roundToAgencyFigure(projected);
    const choseProjection = Boolean(edit) && Boolean(rounded) && Number(edit.amount) === rounded
        && (!edit.currency || !suggestion.currency || edit.currency === suggestion.currency);
    if (edit && !choseProjection) return null;
    return {spent: Number(pace.spend) || 0, days: pace.days, projected: rounded, zero: !rounded,
            last: budget, slower: projected < budget, level: levelOf(deviation, !rounded),
            chosen: choseProjection ? 'pace' : 'last'};
}

/**
 * El color con que nace la línea con el monto del mes pasado, como lo decide el semáforo
 * (`entities/alerts/full_period/budget.py`): sin gasto es siempre rojo (desvío 100 %), y el desvío
 * se redondea a un porcentaje entero antes de comparar (0,245 ya es rojo). Review de UD-22.
 */
function levelOf(deviation, noSpend) {
    if (noSpend) return 'red';
    return Math.round(deviation * 100) >= PACE_CRITICAL_RATIO * 100 ? 'red' : 'amber';
}

/** Cuántas cuentas del paso tienen el aviso: el número del resumen de arriba del paso. */
export function paceNoticeCountOf(pks, budgets, budgetEdits) {
    return (pks || []).filter(pk => paceNoticeOf(budgets?.[String(pk)], budgetEdits?.[pk])).length;
}

/**
 * Con qué se fue el alta: de las cuentas que llegaron a «Crear» con las dos opciones del aviso,
 * cuántas eligieron el ritmo de este mes y cuántas escribieron un monto propio (las demás se quedaron
 * con el del mes pasado). Es la medición de UD-22: hasta acá no se sabía si alguien tocaba «Usar».
 *
 * Las cuentas sin gasto este mes no entran: no tienen las dos opciones (review de UD-22). Y el monto
 * propio se cuenta mirando el aviso SIN la edición, porque con ella `paceNoticeOf` ya no lo devuelve.
 */
export function paceChoicesOf(pks, budgets, budgetEdits) {
    const counts = {withNotice: 0, choseThisMonth: 0, ownAmount: 0};
    (pks || []).forEach(pk => {
        const suggestion = budgets?.[String(pk)];
        const offered = paceNoticeOf(suggestion, undefined);
        if (!offered || offered.zero) return;
        counts.withNotice += 1;
        const edit = budgetEdits?.[pk];
        if (!edit) return;
        if (paceNoticeOf(suggestion, edit)?.chosen === 'pace') counts.choseThisMonth += 1;
        else counts.ownAmount += 1;
    });
    return counts;
}
